"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useId } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import {
	Box,
	Button,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
    useTheme
} from "@/components/controls/base-controls";
import {
	type EmailData,
	emailSchema,
} from "@/core/schemas/register.schema";
import { useRegistrationStore } from "@/core/stores/register.store";
import { Link, useRouter } from "@/i18n/navigation";

const EmailPage = () => {
	const t = useTranslations();

	const email_id = useId();

	const theme = useTheme();
    // Returns true on mobile (xs, sm), false on desktop (md, lg, xl)
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const { registrationData, resetRegistrationData, updateRegistrationData } =
		useRegistrationStore();

	const router = useRouter();

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<EmailData>({
		mode: "onTouched",
		resolver: zodResolver(emailSchema),
		defaultValues: {
			email: registrationData.email ?? "",
		},
	});

	const handleSignInClick = () => {
		reset();
		resetRegistrationData();
		router.push("/sig");
	};

	const onSubmit: SubmitHandler<EmailData> = (data: EmailData) => {
		updateRegistrationData(data);
		router.push("/reg/pwd");
	};

	return (
		<>
			<Typography variant="h3">{t("register.step1")}</Typography>

			<Box
				component="form"
				noValidate
				onSubmit={handleSubmit(onSubmit)}
				sx={{
					pt: "1em",
					width: "100%",
					maxWidth: "400px",
				}}
			>
				<Controller
					name="email"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							fullWidth
							required
							id={email_id}
							label={t("email.label")}
							placeholder={t("email.placeholder")}
							error={!!errors.email}
							helperText={
								errors.email?.message && t(errors.email.message as string)
							}
							slotProps={{ 
								inputLabel: { shrink: isMobile ? true : undefined }
							}}
							sx={{
								margin: "1em 0",
							}}
							variant="outlined"
							inputMode="email"
							type="email"
						/>
					)}
				/>

				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						margin: "2em 0 .5em",
						gap: 2,
					}}
				>
					<Button
						href=""
						variant="square"
						component={Link}
						nativeButton={false}
						disabled={true}
						sx={{
							textTransform: "none",
							width: "100%",
						}}
					>
						{t("register.back")}
					</Button>

					<Button
						variant="contained"
						color="secondary"
						sx={{
							textTransform: "none",
							width: "100%",
						}}
						type="submit"
					>
						{t("register.continue")}
					</Button>
				</Box>
			</Box>

			<Stack
				sx={{
					rowGap: 2,
					width: "100%",
					maxWidth: "400px",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
					}}
				>
					<Box sx={{ fontSize: "24px" }}>&#x291B;</Box>
					<Box sx={{ fontSize: "14px", position: "relative", top: "2px" }}>
						&nbsp;&nbsp;{t("misc.or")}&nbsp;&nbsp;
					</Box>
					<Box sx={{ fontSize: "24px" }}>&#x291C;</Box>
				</Box>

				<Button variant="square" type="button" onClick={handleSignInClick}>
					{t("sign_in.goto")}
				</Button>
			</Stack>
		</>
	);
};

export default EmailPage;
